/*
 * h4h-variant-photo-notice.js
 *
 * OM50 §2b.3 (owner decision, 2026-09-11): when a colour has no photograph of
 * its own, SAY SO, truthfully, rather than pretending or hiding it.
 *
 * WHY THIS EXISTS, measured rather than assumed. Of 1,022 products carrying a
 * Color option: 707 have a photograph for every colour, 291 have none at all,
 * and 24 are mixed. Ella's own variant picker DOES switch the featured image
 * when the variant carries one -- proven on the preview by clicking "Military
 * Green" on a 72-variant sweatshirt and watching the active slide change from
 * `...SW2D.Black_glowua.jpg` to `...SW2D.MilitaryGreen_rnokin.jpg`, with the
 * SKU moving from `.SB` to `.SMG`. So there is no theme bug to fix; what is
 * left is product data, and no theme can switch to a photo that does not exist.
 *
 * The owner considered marking those colours out of stock and chose the
 * truthful notice instead. Marking them would have taken 19,125 of 70,655
 * variants out of sale and made 291 products unbuyable, while being untrue --
 * they are in stock and buyable today.
 *
 * WHAT IT SAYS, AND WHAT IT NEVER SAYS
 *   * it is a statement of fact about the PHOTOGRAPHY on this page;
 *   * it never says out of stock, coming soon, unavailable, or names a date;
 *   * both colour names are read from the product's own data, never hardcoded;
 *   * when the selected colour HAS a photograph it renders nothing at all.
 *
 * DATA SOURCE. Ella already prints the full variant array into the page as an
 * `application/json` script (84 KB on the product used to develop this), so the
 * notice costs no extra request. If that array cannot be found the script does
 * nothing -- a missing notice is a far better failure than a wrong one.
 */
(function () {
  'use strict';

  var NOTICE_CLASS = 'h4h-photo-notice';
  var COLOUR_RE = /^colou?r$/i;

  function parseVariants() {
    var scripts = document.querySelectorAll('script[type="application/json"]');
    for (var i = 0; i < scripts.length; i++) {
      var text = scripts[i].textContent;
      if (!text || text.length < 40 || text.indexOf('"option1"') === -1) continue;
      try {
        var data = JSON.parse(text);
        if (Array.isArray(data) && data.length && typeof data[0] === 'object' &&
            'option1' in data[0]) {
          return data;
        }
      } catch (e) { /* not ours */ }
    }
    return null;
  }

  /* Which option position holds the colour? Read it from the picker's own
     legend ("Color: Black"), falling back to position 1. */
  function colourOptionIndex() {
    var legends = document.querySelectorAll('legend, .form__label, .variant-option__label');
    for (var i = 0; i < legends.length; i++) {
      var name = (legends[i].textContent || '').split(':')[0].trim();
      if (COLOUR_RE.test(name)) {
        var fs = legends[i].closest('fieldset, .product-form__input');
        if (!fs) continue;
        var all = fs.parentElement ? fs.parentElement.querySelectorAll('fieldset, .product-form__input') : [];
        for (var j = 0; j < all.length; j++) {
          if (all[j] === fs) return { index: j + 1, fieldset: fs };
        }
        return { index: 1, fieldset: fs };
      }
    }
    return null;
  }

  function selectedColour(fieldset) {
    var checked = fieldset.querySelector('input:checked');
    if (checked && checked.value) return checked.value;
    var legend = fieldset.querySelector('legend');
    if (legend && legend.textContent.indexOf(':') > -1) {
      return legend.textContent.split(':').slice(1).join(':').trim();
    }
    return null;
  }

  function build() {
    var variants = parseVariants();
    var picked = colourOptionIndex();
    if (!variants || !picked) return;

    var key = 'option' + picked.index;
    var photographed = {};     // colour -> true when some variant carries an image
    var firstPhotographed = null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var colour = v[key];
      if (!colour) continue;
      if (v.featured_image || v.featured_media) {
        photographed[colour] = true;
        if (firstPhotographed === null) firstPhotographed = colour;
      } else if (!(colour in photographed)) {
        photographed[colour] = false;
      }
    }

    function render() {
      var colour = selectedColour(picked.fieldset);
      var old = picked.fieldset.parentElement.querySelector('.' + NOTICE_CLASS);
      if (old) old.parentElement.removeChild(old);
      if (!colour || photographed[colour]) return;   // photographed: say nothing

      var line;
      if (firstPhotographed) {
        line = 'Photo shows ' + firstPhotographed + '. This item is also printed on ' + colour + '.';
      } else {
        line = 'The photographs above are not specific to each colour. ' +
               'This item is printed on ' + colour + '.';
      }
      var p = document.createElement('p');
      p.className = NOTICE_CLASS;
      p.setAttribute('role', 'status');
      p.textContent = line;
      picked.fieldset.insertAdjacentElement('afterend', p);
    }

    render();

    /* Ella re-renders the variant picker's markup from the server on a variant
       change, which silently removes anything inserted next to it -- the first
       version of this file inserted the notice correctly and then lost it,
       measured as `document.querySelector('.h4h-photo-notice') === null` on a
       page where the same logic run by hand inserted it fine. So the picker is
       observed and the notice re-applied, rather than written once and trusted.
       The observer is debounced and re-resolves the fieldset each time, because
       the node it points at is exactly what gets replaced. */
    var pending = null;
    function schedule() {
      if (pending) clearTimeout(pending);
      pending = setTimeout(function () {
        pending = null;
        var again = colourOptionIndex();
        if (again) picked = again;
        render();
      }, 80);
    }

    document.addEventListener('change', schedule, true);
    document.addEventListener('click', schedule, true);
    window.addEventListener('popstate', schedule);

    var host = picked.fieldset.closest('variant-selects, .product-form__input')
               || picked.fieldset.parentElement;
    if (host && window.MutationObserver) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          for (var j = 0; j < records[i].addedNodes.length; j++) {
            if (records[i].addedNodes[j].nodeType === 1 &&
                records[i].addedNodes[j].classList &&
                records[i].addedNodes[j].classList.contains(NOTICE_CLASS)) {
              return;   /* our own insertion; do not loop on it */
            }
          }
        }
        schedule();
      }).observe(host.parentElement || host, { childList: true, subtree: true });
    }
  }

  function start() {
    try {
      build();
    } catch (e) {
      /* a missing notice is a better failure than a wrong one */
      if (window.console && console.warn) console.warn('[h4h] photo notice: ', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  window.addEventListener('load', start);
})();
